import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSuperAdminUser } from '../utils/userRoles';
import {
  getUsers,
  updateUser,
  getRegistrationTypePageAccess,
  updateRegistrationTypePageAccess,
} from '../services/api';
import {
  REGISTRATION_TYPES,
  formatRegistrationTypeLabel,
  workspacePathForRegistrationType,
} from '../utils/registrationTypes';
import {
  REGISTRATION_TYPE_PAGE_CONFIG,
  CRITICAL_MINERALS_PAGE_FEATURES,
  EV_PAGE_FEATURES,
  enabledPlatformPages,
  hasToggleablePageAccess,
  isCorporateRegistrationType,
  toggleableFeaturesForRegistrationType,
} from '../utils/registrationTypePages';
import { formatCorporatePageLabel } from '../utils/corporatePageAccess';
import { formatPlanFeatureLabel } from '../utils/planFeatureLabels';
import { Pencil, X, Check, ExternalLink } from 'lucide-react';

const USER_TYPES = ['user', 'admin', 'superadmin'];

function FeatureCheckboxGrid({ features, enabledSet, isEditing, onToggle, formatLabel }) {
  const labelFor = formatLabel || formatPlanFeatureLabel;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {features.map((feature) => {
        const checked = enabledSet.has(feature);
        const label = labelFor(feature);
        return (
          <label
            key={feature}
            className={`flex items-center gap-2 text-sm rounded-md px-2 py-1.5 ${
              isEditing
                ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'
                : checked
                  ? 'text-gray-800 dark:text-gray-200'
                  : 'text-gray-400 dark:text-gray-500 line-through opacity-60'
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={!isEditing}
              onChange={() => onToggle(feature)}
              className="rounded border-gray-300 text-[#163248] focus:ring-[#163248] dark:border-gray-600"
            />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

function RegistrationTypePagesPanel({
  registrationType,
  config,
  enabledFeatures,
  isEditing,
  featuresDraft,
  onStartEdit,
  onSave,
  onCancel,
  onToggleFeature,
  saving,
}) {
  const typeLabel = formatRegistrationTypeLabel(registrationType);
  const isCorporate = isCorporateRegistrationType(registrationType);
  const canToggle = hasToggleablePageAccess(registrationType);
  const toggleableFeatures = toggleableFeaturesForRegistrationType(registrationType);
  const corporatePageFeatures = isCorporate
    ? toggleableFeatures.filter((f) => f.startsWith('corporate:'))
    : [];
  const criticalMineralsFeatures = !isCorporate
    ? CRITICAL_MINERALS_PAGE_FEATURES.filter((f) => toggleableFeatures.includes(f))
    : [];
  const evFeatures = EV_PAGE_FEATURES.filter((f) => toggleableFeatures.includes(f));
  const activeFeatures = isEditing ? featuresDraft : enabledFeatures;
  const draftSet = new Set(activeFeatures.map((f) => f.toLowerCase()));
  const enabledPlatformRoutes = enabledPlatformPages(activeFeatures);
  const enabledPages = !isCorporate ? enabledPlatformRoutes : [];
  const enabledCorporateLabels = isCorporate
    ? corporatePageFeatures.filter((f) => draftSet.has(f)).map(formatCorporatePageLabel)
    : [];
  const enabledCorporateEvRoutes = isCorporate
    ? enabledPlatformRoutes.filter((p) => EV_PAGE_FEATURES.includes(p.feature))
    : [];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{typeLabel}</h3>
          <Link
            to={config.workspacePath}
            className="inline-flex items-center gap-1 mt-1 text-sm text-[#163248] dark:text-primary-400 hover:underline"
          >
            {config.workspaceTitle}
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
        {canToggle && !isEditing ? (
          <button
            type="button"
            onClick={onStartEdit}
            className="p-1.5 text-[#163248] dark:text-primary-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded shrink-0"
            title="Edit page access"
          >
            <Pencil className="h-4 w-4" />
          </button>
        ) : canToggle && isEditing ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="p-1.5 text-green-600 dark:text-green-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50"
              title="Save page access"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="p-1.5 text-red-600 dark:text-red-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="p-5 space-y-5">
        {isCorporate && corporatePageFeatures.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              {config.pageSectionTitle || 'Corporate pages'}
            </p>
            <FeatureCheckboxGrid
              features={corporatePageFeatures}
              enabledSet={draftSet}
              isEditing={isEditing}
              onToggle={onToggleFeature}
              formatLabel={formatCorporatePageLabel}
            />
          </div>
        )}

        {criticalMineralsFeatures.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              {config.pageSectionTitle || 'Critical minerals platform pages'}
            </p>
            <FeatureCheckboxGrid
              features={criticalMineralsFeatures}
              enabledSet={draftSet}
              isEditing={isEditing}
              onToggle={onToggleFeature}
            />
          </div>
        )}

        {evFeatures.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Electric vehicles (EV)
            </p>
            <FeatureCheckboxGrid
              features={evFeatures}
              enabledSet={draftSet}
              isEditing={isEditing}
              onToggle={onToggleFeature}
            />
          </div>
        )}

        {!isEditing && isCorporate && (enabledCorporateLabels.length > 0 || enabledCorporateEvRoutes.length > 0) && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            {enabledCorporateLabels.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Enabled corporate pages
                </p>
                <ul className="flex flex-wrap gap-2">
                  {enabledCorporateLabels.map((label) => (
                    <li
                      key={label}
                      className="text-xs px-2 py-1 rounded-full bg-[#163248]/10 dark:bg-primary-900/40 text-[#163248] dark:text-primary-300"
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {enabledCorporateEvRoutes.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Enabled EV routes
                </p>
                <ul className="flex flex-wrap gap-2">
                  {enabledCorporateEvRoutes.map((page) => (
                    <li key={page.feature}>
                      <Link
                        to={page.path}
                        className="text-xs px-2 py-1 rounded-full bg-[#163248]/10 dark:bg-primary-900/40 text-[#163248] dark:text-primary-300 hover:underline"
                      >
                        {page.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!isEditing && !isCorporate && enabledPages.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Enabled routes
            </p>
            <ul className="flex flex-wrap gap-2">
              {enabledPages.map((page) =>
                page.path ? (
                  <li key={page.feature}>
                    <Link
                      to={page.path}
                      className="text-xs px-2 py-1 rounded-full bg-[#163248]/10 dark:bg-primary-900/40 text-[#163248] dark:text-primary-300 hover:underline"
                    >
                      {page.label}
                    </Link>
                  </li>
                ) : (
                  <li
                    key={page.feature}
                    className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    {page.label}
                  </li>
                ),
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const UserManagementPage = () => {
  const { user: currentUser, isLoading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [regTypeFeatures, setRegTypeFeatures] = useState({ corporate: [], policy_maker: [] });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingRegType, setEditingRegType] = useState(null);
  const [featuresDraft, setFeaturesDraft] = useState([]);
  const [savingRegType, setSavingRegType] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    user_type: 'user',
    registration_type: '',
    is_active: true,
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadPageAccess = useCallback(() => {
    return getRegistrationTypePageAccess()
      .then((res) => {
        if (!res.success || !res.registration_types) return;
        const map = { corporate: [], policy_maker: [] };
        res.registration_types.forEach((item) => {
          const key = item.registration_type;
          if (key in map) {
            map[key] = Array.isArray(item.features) ? item.features : [];
          }
        });
        setRegTypeFeatures(map);
      })
      .catch(() => setError('Failed to load page access settings'));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSuperAdminUser(currentUser)) {
      setLoading(false);
      return;
    }
    Promise.all([getUsers(), loadPageAccess()])
      .then(([usersRes]) => {
        if (usersRes.success && usersRes.users) setUsers(usersRes.users);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  }, [authLoading, currentUser, navigate, loadPageAccess]);

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({
      full_name: u.full_name || '',
      email: u.email || '',
      user_type: u.user_type || 'user',
      registration_type: u.registration_type || 'corporate',
      is_active: u.is_active !== false,
    });
    setError('');
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      full_name: '',
      email: '',
      user_type: 'user',
      registration_type: '',
      is_active: true,
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const regType = editForm.registration_type;
    if (!REGISTRATION_TYPES.some((t) => t.value === regType)) {
      setError('Registration type must be Corporate or Policy Maker');
      return;
    }
    setError('');
    setMessage('');
    try {
      const res = await updateUser(editingId, {
        full_name: editForm.full_name,
        email: editForm.email,
        user_type: editForm.user_type,
        registration_type: regType,
        is_active: editForm.is_active,
      });
      if (res.success && res.user) {
        setUsers((prev) => prev.map((u) => (u.id === editingId ? { ...u, ...res.user } : u)));
        setMessage('User updated.');
        setEditingId(null);
      } else {
        setError('Update failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const startRegTypeEdit = (registrationType) => {
    setEditingRegType(registrationType);
    setFeaturesDraft([...(regTypeFeatures[registrationType] || [])]);
    setError('');
    setMessage('');
  };

  const cancelRegTypeEdit = () => {
    setEditingRegType(null);
    setFeaturesDraft([]);
  };

  const toggleFeature = (feature) => {
    setFeaturesDraft((prev) => {
      const key = feature.toLowerCase();
      if (prev.map((f) => f.toLowerCase()).includes(key)) {
        return prev.filter((f) => f.toLowerCase() !== key);
      }
      return [...prev, key];
    });
  };

  const saveRegTypeFeatures = async (registrationType) => {
    setSavingRegType(true);
    setError('');
    setMessage('');
    try {
      const res = await updateRegistrationTypePageAccess(registrationType, featuresDraft);
      if (res.success) {
        const saved =
          (res.plan?.features && Array.isArray(res.plan.features) ? res.plan.features : featuresDraft) ||
          featuresDraft;
        setRegTypeFeatures((prev) => ({ ...prev, [registrationType]: saved }));
        setMessage(`${formatRegistrationTypeLabel(registrationType)} page access updated.`);
        setEditingRegType(null);
        setFeaturesDraft([]);
        await refreshUser();
      } else {
        setError('Failed to update page access');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update page access');
    } finally {
      setSavingRegType(false);
    }
  };

  if (!currentUser) return null;
  if (!isSuperAdminUser(currentUser)) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">User Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Manage users (Corporate or Policy Maker) and control which platform pages each registration
          type can access. Users inherit page access from their registration type.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 text-green-700 dark:text-green-300 rounded">
            {message}
          </div>
        )}

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">Users</h2>
          {loading ? (
            <div className="text-gray-500 dark:text-gray-400">Loading users...</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Username
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Registration type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Workspace
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'opacity-60' : ''}>
                      {editingId === u.id ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              value={editForm.full_name}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, full_name: e.target.value }))
                              }
                              className="w-full px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, email: e.target.value }))
                              }
                              className="w-full px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm">{u.username}</td>
                          <td className="px-4 py-2">
                            <select
                              value={editForm.user_type}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, user_type: e.target.value }))
                              }
                              className="px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                              {USER_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={editForm.registration_type}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, registration_type: e.target.value }))
                              }
                              className="px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                              {REGISTRATION_TYPES.map((rt) => (
                                <option key={rt.value} value={rt.value}>
                                  {rt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">—</td>
                          <td className="px-4 py-2">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={editForm.is_active}
                                onChange={(e) =>
                                  setEditForm((f) => ({ ...f, is_active: e.target.checked }))
                                }
                                className="rounded"
                              />
                              <span className="text-sm">Active</span>
                            </label>
                          </td>
                          <td className="px-4 py-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={saveEdit}
                              className="p-1 text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              title="Save"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="p-1 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 text-sm">{u.full_name}</td>
                          <td className="px-4 py-2 text-sm">{u.email}</td>
                          <td className="px-4 py-2 text-sm">{u.username}</td>
                          <td className="px-4 py-2 text-sm">{u.user_type}</td>
                          <td className="px-4 py-2 text-sm">
                            {formatRegistrationTypeLabel(u.registration_type)}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <Link
                              to={workspacePathForRegistrationType(u.registration_type)}
                              className="text-[#163248] dark:text-primary-400 hover:underline"
                            >
                              Open workspace
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {u.is_active !== false ? 'Active' : 'Inactive'}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => startEdit(u)}
                              className="p-1 text-[#163248] dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              title="Edit user"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Page access by registration type</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          This section lets you control which pages are visible to each user type. For Corporate users, you can toggle access to Corporate Pages and optional Electric Vehicles (EV) pages. For Policy Maker users, you can manage access to Critical Minerals Platform pages and optional EV pages. The currently enabled pages for each type are displayed as tags at the bottom of the respective panel. Click the pencil icon to make changes — note that affected users may need to sign in again for the updates to take effect.          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {REGISTRATION_TYPES.map(({ value }) => (
              <RegistrationTypePagesPanel
                key={value}
                registrationType={value}
                config={REGISTRATION_TYPE_PAGE_CONFIG[value]}
                enabledFeatures={regTypeFeatures[value] || []}
                isEditing={editingRegType === value}
                featuresDraft={featuresDraft}
                onStartEdit={() => startRegTypeEdit(value)}
                onSave={() => saveRegTypeFeatures(value)}
                onCancel={cancelRegTypeEdit}
                onToggleFeature={toggleFeature}
                saving={savingRegType && editingRegType === value}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserManagementPage;
